/**
 * What a vehicle card can say about pricing and quoting *before* you open it.
 *
 * Pure — takes a vehicle's `vehicle_pricing` rows plus the account's custom-quote
 * switch and returns display-ready text. Kept out of the card component so the
 * two rules an operator actually relies on stay unit-testable:
 *
 *  1. A price only counts if its pricing row is enabled. A vehicle whose rows are
 *     all disabled is invisible in the booking flow (the viewer filters out any
 *     vehicle with no priced total), and the card has to say so — that silent
 *     disappearance is the single most confusing thing about this screen.
 *  2. Whether a vehicle is quote-only is NOT `vehicles.is_quote_only`. That column
 *     does nothing while the account's `booking_custom_quotes_enabled` switch is
 *     off, so the card reports the EFFECTIVE state and flags configuration that
 *     currently has no effect, rather than promising a Get Quote button the
 *     customer will never see.
 */

import {
  isQuoteOnlyVehicle,
  resolveVehicleQuoteDistanceThreshold,
  type VehicleCustomQuoteSettings,
} from '@limo/shared'

/** The `vehicle_pricing` columns the preview reads. */
export interface VehiclePricingPreviewRow {
  readonly pricing_type: string
  readonly is_active?: boolean | null
  // Postgres `numeric` arrives as a string through PostgREST, so every money
  // field is widened and coerced rather than trusted to be a number.
  readonly base_price?: number | string | null
  readonly price_per_mile?: number | string | null
  readonly price_per_hour?: number | string | null
  readonly minimum_miles?: number | string | null
  readonly minimum_hours?: number | string | null
  readonly service_fee?: number | string | null
  readonly use_tiered_pricing?: boolean | null
}

export interface PricingPreviewLine {
  readonly pricingType: 'point_to_point' | 'hourly'
  /** "Point to point" | "Hourly" */
  readonly label: string
  /** Ordered fragments, e.g. ["$85 base", "$3.50/mi", "10 mi min"]. */
  readonly parts: readonly string[]
}

export interface VehiclePricingPreview {
  readonly lines: readonly PricingPreviewLine[]
  /** False when no pricing type is enabled — see rule 1 above. */
  readonly hasActivePricing: boolean
}

const PRICING_TYPE_ORDER = ['point_to_point', 'hourly'] as const

const LABELS: Record<(typeof PRICING_TYPE_ORDER)[number], string> = {
  point_to_point: 'Point to point',
  hourly: 'Hourly',
}

/** A finite, positive number, or null. Rejects '', null, NaN and negatives. */
function positiveNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Money for a dense card: no cents on whole amounts ("$85"), cents when they
 * carry meaning ("$3.50"), separators on large ones ("$1,250").
 */
export function formatPreviewMoney(amount: number): string {
  const hasCents = Math.round(amount * 100) % 100 !== 0
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

function summarizeRow(row: VehiclePricingPreviewRow): PricingPreviewLine | null {
  const pricingType = row.pricing_type === 'hourly' ? 'hourly' : 'point_to_point'
  const parts: string[] = []

  const base = positiveNumber(row.base_price)
  if (base !== null) parts.push(`${formatPreviewMoney(base)} base`)

  if (pricingType === 'point_to_point') {
    // Tiered mileage replaces the flat per-mile rate, so showing a per-mile
    // figure alongside it would misstate what the customer is charged.
    if (row.use_tiered_pricing) {
      parts.push('tiered mileage')
    } else {
      const perMile = positiveNumber(row.price_per_mile)
      if (perMile !== null) parts.push(`${formatPreviewMoney(perMile)}/mi`)
    }

    const minMiles = positiveNumber(row.minimum_miles)
    if (minMiles !== null) parts.push(`${minMiles} mi min`)
  } else {
    const perHour = positiveNumber(row.price_per_hour)
    if (perHour !== null) parts.push(`${formatPreviewMoney(perHour)}/hr`)

    const minHours = positiveNumber(row.minimum_hours)
    if (minHours !== null) parts.push(`${minHours} hr min`)
  }

  const serviceFee = positiveNumber(row.service_fee)
  if (serviceFee !== null) parts.push(`${formatPreviewMoney(serviceFee)} fee`)

  // An enabled row with every figure blank or zero tells the operator nothing;
  // say so rather than rendering a bare label.
  if (parts.length === 0) parts.push('no amounts set')

  return { pricingType, label: LABELS[pricingType], parts }
}

/**
 * Summarize a vehicle's pricing rows for the card.
 *
 * Only ENABLED rows produce a line, in a stable order (point-to-point, then
 * hourly) regardless of how PostgREST returned them.
 */
export function summarizeVehiclePricing(
  rows: readonly VehiclePricingPreviewRow[] | null | undefined
): VehiclePricingPreview {
  const active = (rows ?? []).filter((row) => row.is_active !== false)

  const lines = PRICING_TYPE_ORDER.flatMap((type) => {
    const row = active.find((r) => r.pricing_type === type)
    if (!row) return []
    const line = summarizeRow(row)
    return line ? [line] : []
  })

  return { lines, hasActivePricing: lines.length > 0 }
}

/**
 * How this vehicle gets priced in the customer's booking flow.
 *
 * `effective: false` means the vehicle carries the setting but the account's
 * custom-quotes switch is off, so it currently changes nothing.
 */
export type VehicleQuoteMode =
  | { readonly kind: 'calculated' }
  | { readonly kind: 'quote_only'; readonly effective: boolean }
  | { readonly kind: 'distance'; readonly miles: number; readonly effective: boolean }

/**
 * Resolve the quote mode shown on the card.
 *
 * The validity rules (a truthy `is_quote_only`, a usable positive mileage) are
 * borrowed from the shared pricing module by asking it what WOULD apply with the
 * feature on — so an unusable stored mileage is ignored here exactly as the
 * booking flow ignores it, and `effective` is left as the one thing the switch
 * decides. Re-implementing those checks locally is how the Builder and the
 * booking flow would start disagreeing.
 */
export function describeVehicleQuoteMode(args: {
  readonly customQuotesEnabled: boolean
  readonly vehicle: VehicleCustomQuoteSettings | null | undefined
}): VehicleQuoteMode {
  const { customQuotesEnabled, vehicle } = args

  if (isQuoteOnlyVehicle({ customQuotesEnabled: true, vehicle })) {
    return { kind: 'quote_only', effective: customQuotesEnabled }
  }

  const threshold = resolveVehicleQuoteDistanceThreshold({
    customQuotesEnabled: true,
    vehicle,
  })
  if (threshold) {
    return { kind: 'distance', miles: threshold.miles, effective: customQuotesEnabled }
  }

  return { kind: 'calculated' }
}

/**
 * True when the vehicle is hidden from the booking flow by its pricing.
 *
 * A quote-only vehicle is exempt: the viewer keeps it in the list precisely
 * because the operator prices it by hand, so it needs no enabled pricing row.
 */
export function isHiddenByPricing(args: {
  readonly preview: VehiclePricingPreview
  readonly quoteMode: VehicleQuoteMode
}): boolean {
  const quoteOnlyInEffect = args.quoteMode.kind === 'quote_only' && args.quoteMode.effective
  return !quoteOnlyInEffect && !args.preview.hasActivePricing
}
